import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../config/firebase';
import { onAuthStateChanged, signOut, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';

const Header = ({ darkMode, toggleDarkMode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const menuRef = useRef(null);

  const AUTHORIZED_ADMIN_EMAILS = ['doctorworldconsultancy@gmail.com', 'officialvishwateja@gmail.com'];

  // Handle auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isAdmin = user && AUTHORIZED_ADMIN_EMAILS.includes(user.email);

  // Handle outside clicks to close mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Menu toggle function
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const MenuIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );

  const CloseIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );

  const SunIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="5"/>
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
    </svg>
  );

  const MoonIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );

  return (
    <header className="relative z-50 sticky top-0">
      {/* Dynamic glassmorphism background */}
      <div className={`absolute inset-0 ${darkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-md border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} shadow-lg`}></div>
      {darkMode && <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 via-purple-900/20 to-indigo-900/20"></div>}
      
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Professional Logo */}
          <Link to="/" className="flex items-center group transition-all duration-300 hover:opacity-90 flex-shrink-0">
            <div className="flex flex-col">
              <span className={`text-[16px] sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} tracking-tight sm:tracking-wide font-heading whitespace-nowrap`}>
                Doctor World Consultancy
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex md:items-center md:space-x-2 xl:space-x-4">
            <Link to="/" className={`px-2 xl:px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-all duration-300 font-medium`}>
              Home
            </Link>
            <Link to="/student-form" className={`px-2 xl:px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-all duration-300 font-medium`}>
              Apply Now
            </Link>
            <Link to="/university-fees" className={`px-2 xl:px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-all duration-300 font-medium`}>
              Universities
            </Link>

            {isAdmin && (
              <Link to="/admin-dashboard" className={`px-2 xl:px-4 py-2 ${darkMode ? 'text-pink-400 hover:text-pink-300 hover:bg-pink-500/10' : 'text-pink-600 hover:text-pink-700 hover:bg-pink-50'} rounded-lg transition-all duration-300 font-bold whitespace-nowrap`}>
                Admin Panel
              </Link>
            )}

            <Link to="/status-check" className="flex items-center gap-2 px-4 xl:px-6 py-2 xl:py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-105 whitespace-nowrap">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Check Status
            </Link>

            <div className="h-6 w-px bg-gray-300 dark:bg-gray-700 mx-2"></div>
            
            {/* User Profile & Auth */}
            {user ? (
              <div className="flex items-center space-x-3 ml-2">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="Profile" className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600" title={user.email} />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500 flex items-center justify-center" title={user.email}>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{user.email?.charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <button
                  onClick={handleSignOut}
                  className={`text-sm font-medium px-3 py-1.5 rounded-lg border ${darkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-200 text-red-600 hover:bg-red-50'} transition-colors whitespace-nowrap`}
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={handleGoogleSignIn}
                className={`flex items-center gap-2 px-3 xl:px-4 py-2 border rounded-lg transition-colors ml-2 whitespace-nowrap shadow-sm hover:shadow ${darkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className={`text-sm md:text-base font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Sign in</span>
              </button>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 ml-2 ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-all duration-200`}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>

          {/* Mobile menu buttons */}
          <div className="flex items-center lg:hidden space-x-2">
            {user && (
              <div className="flex items-center">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="Profile" className="w-7 h-7 rounded-full border border-gray-300 dark:border-gray-600 mr-2" />
                ) : null}
              </div>
            )}
            <button
              onClick={toggleDarkMode}
              className={`p-2 ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-all duration-200`}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <SunIcon /> : <MoonIcon />}
            </button>
            <button
              onClick={toggleMenu}
              className={`p-2 rounded-lg ${darkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'} transition-colors duration-300`}
              aria-label="Toggle mobile menu"
            >
              {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div ref={menuRef} className="lg:hidden">
            <div className={`px-2 pt-2 pb-4 space-y-1 ${darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'} backdrop-blur-lg border-t shadow-lg overflow-y-auto max-h-[calc(100vh-64px)]`}>
              <Link
                to="/"
                className={`block px-4 py-3 ${darkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-colors duration-300 font-medium`}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/student-form"
                className={`block px-4 py-3 ${darkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-colors duration-300 font-medium`}
                onClick={() => setIsMenuOpen(false)}
              >
                Apply Now
              </Link>
              <Link
                to="/university-fees"
                className={`block px-4 py-3 ${darkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-colors duration-300 font-medium`}
                onClick={() => setIsMenuOpen(false)}
              >
                Universities
              </Link>

              {isAdmin && (
                <Link
                  to="/admin-dashboard"
                  className={`block px-4 py-3 ${darkMode ? 'text-pink-400 hover:bg-white/10' : 'text-pink-600 hover:bg-gray-100'} rounded-lg transition-colors duration-300 font-bold`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Admin Panel
                </Link>
              )}

              <Link
                to="/status-check"
                className="flex items-center justify-center gap-2 px-4 py-3 mx-2 my-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-colors duration-300 text-center shadow-lg shadow-blue-500/25"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Check Status
              </Link>

              {user ? (
                <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 overflow-hidden">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="Profile" className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{user.email?.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <span className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{user.email}</span>
                    </div>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className={`text-sm font-medium px-3 py-1.5 rounded-lg border flex-shrink-0 ml-2 ${darkMode ? 'border-red-500/30 text-red-400' : 'border-red-200 text-red-600'}`}
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              ) : (
                <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700 px-4">
                  <button
                    onClick={() => {
                      handleGoogleSignIn();
                      setIsMenuOpen(false);
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 border rounded-xl transition-colors shadow-sm hover:shadow ${darkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Sign in with Google</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
